import {
  type FormEvent,
  type JSX,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { AppLink, useNavigator } from '../../discovery/router';
import { routes } from '../../routes';
import { SessionApiError } from '../../session/client';
import { type SessionUser, initialsOf, useSession } from '../../session/SessionProvider';
import {
  type Address,
  type AddressInput,
  ORDERS_PAGE,
  addAddress,
  addContact,
  changePassword,
  editAddress,
  getConsents,
  getDeviceOrderCount,
  getFactors,
  getInbox,
  getProfile,
  getUpcomingBookings,
  getWallet,
  grantConsent,
  logoutOthers,
  makeDefaultAddress,
  readAll,
  readOne,
  removeAddress,
  removeContact,
  updateProfile,
  withdrawConsent,
} from './account-api';
import { MyClinicsEntry } from './ClinicsPage';
import './account.css';

/**
 * The Account page, signed in — the `Just Treated Account` frame.
 *
 * Built from the frame's own markup, section by section, over the platform's
 * real records. Where the frame draws something the platform does not have,
 * the element is kept in its place and says "Coming soon" — it never shows the
 * frame's demo person, cards, saved items, vouchers or credit:
 *
 * - **Payment methods** — no saved cards until payments are built (owner D2).
 * - **Saved** — there is no saved-items or saved-providers store yet.
 * - **Prescriptions tile** — the Prescriptions page and its records are a later stage.
 * - **Two-step verification** — shown as it really is (from the account's
 *   factors); turning it on or off from the website is not built.
 * - **Marketing from providers**, **Download my data**, **Delete account** — not built.
 * - The email address is the sign-in identity and cannot be changed here.
 * - Order, appointment and prescription messages are transactional: they are
 *   always sent, so the matrix states that instead of drawing switches that
 *   would claim they can be turned off.
 */

// -- The frame's icons and sections -------------------------------------------

const IC = {
  home: 'M4 11 12 4l8 7v9h-5v-6H9v6H4Z',
  work: 'M4 8h16v12H4zM9 8V5h6v3',
  pin: 'M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0c0 5.4-6.5 11-6.5 11ZM12 12.4a2.4 2.4 0 1 0 0-4.8 2.4 2.4 0 0 0 0 4.8',
  person: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM4.5 20a7.5 7.5 0 0 1 15 0',
  card: 'M3.5 6.5h17v11h-17zM3.5 10h17M7 14.5h4',
  bell: 'M6 16V11a6 6 0 0 1 12 0v5l1.5 2h-15ZM10 20.5h4',
  heart: 'M12 20s-7.5-4.6-7.5-10.2A4.3 4.3 0 0 1 12 7.2a4.3 4.3 0 0 1 7.5 2.6C19.5 15.4 12 20 12 20Z',
  shield: 'M12 3.2 5 6v5.4c0 4.4 3 8 7 9.4 4-1.4 7-5 7-9.4V6Z',
  grid: 'M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z',
  box: 'M5 8h14l-1 12H6ZM9 8V6a3 3 0 0 1 6 0v2',
  cal: 'M4.5 6h15v14h-15zM4.5 10h15M8.5 3.5v4M15.5 3.5v4',
  rx: 'M9 3h6v4H9zM6 5h12v16H6zM9 12h6M12 9v6',
  star: 'M12 3.5 14.2 9l5.8.4-4.4 3.8 1.4 5.6L12 15.8l-5 3 1.4-5.6L4 9.4 9.8 9Z',
} as const;

export const ACCOUNT_SECTIONS = [
  ['home', 'Overview', IC.grid],
  ['details', 'Personal details', IC.person],
  ['addresses', 'Addresses', IC.pin],
  ['payments', 'Payment methods', IC.card],
  ['notifications', 'Notifications', IC.bell],
  ['saved', 'Saved', IC.heart],
  ['privacy', 'Privacy and security', IC.shield],
] as const;

export type AccountSection = (typeof ACCOUNT_SECTIONS)[number][0];

function sectionFrom(value: string | null): AccountSection {
  return ACCOUNT_SECTIONS.some(([id]) => id === value) ? (value as AccountSection) : 'home';
}

function sectionHref(id: AccountSection): string {
  return id === 'home' ? routes.account.path : `${routes.account.path}?section=${id}`;
}

function Icon({
  d,
  stroke = '#5b2ede',
  size = 17,
}: {
  d: string;
  stroke?: string;
  size?: number;
}): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={stroke}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}

// -- Small helpers -------------------------------------------------------------

/** A name the person gave. Older accounts were given their email as a name; that is not one. */
export function realName(value: string | undefined): string | undefined {
  const trimmed = value?.trim() ?? '';
  return trimmed === '' || trimmed.includes('@') ? undefined : trimmed;
}

/** YYYY-MM-DD → DD/MM/YYYY. Anything else is shown as it is rather than blanked. */
export function displayDate(iso: string | undefined): string {
  if (iso === undefined) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m === null ? iso : `${m[3]}/${m[2]}/${m[1]}`;
}

/** DD/MM/YYYY → YYYY-MM-DD, or undefined for a date that is not real or is in the future. */
export function isoDate(typed: string, today = new Date()): string | undefined {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(typed.trim());
  if (m === null) return undefined;
  const [day, month, year] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
    return undefined;
  }
  if (date.getTime() > today.getTime() || year < 1900) return undefined;
  return `${String(year)}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function when(iso: string, now = Date.now()): string {
  const days = Math.floor((now - new Date(iso).getTime()) / 86_400_000);
  if (Number.isNaN(days)) return '';
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${String(days)} days ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function messageOf(error: unknown): string {
  return error instanceof SessionApiError ? error.message : 'Something went wrong. Try again.';
}

const UK_POSTCODE = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;

function addressLine(a: Address): string {
  return [a.line1, a.line2, `${a.city} ${a.postcode}`].filter((p) => p !== undefined && p !== '').join(', ');
}

function addressIcon(label: string): string {
  if (/home/i.test(label)) return IC.home;
  if (/work/i.test(label)) return IC.work;
  return IC.pin;
}

interface Loaded<T> {
  readonly value?: T;
  readonly error?: string;
  readonly loading: boolean;
  reload(): void;
}

/** One record, loaded once and reloadable. Its failure is its own: the rest of the page still works. */
function useLoaded<T>(load: () => Promise<T>): Loaded<T> {
  const [state, setState] = useState<{ value?: T; error?: string; loading: boolean }>({ loading: true });
  const loadRef = useRef(load);
  loadRef.current = load;
  const alive = useRef(true);

  const reload = useCallback(() => {
    setState((previous) => ({ ...previous, loading: true }));
    loadRef
      .current()
      .then((value) => {
        if (alive.current) setState({ value, loading: false });
      })
      .catch((error: unknown) => {
        if (alive.current) setState({ error: messageOf(error), loading: false });
      });
  }, []);

  useEffect(() => {
    alive.current = true;
    reload();
    return () => {
      alive.current = false;
    };
  }, [reload]);

  return { ...state, reload };
}

function useWide(): boolean {
  const read = (): boolean => (typeof window === 'undefined' ? true : window.innerWidth >= 900);
  const [wide, setWide] = useState(read);
  useEffect(() => {
    const onResize = (): void => setWide(read());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return wide;
}

// -- Modal ---------------------------------------------------------------------

interface ModalField {
  readonly key: string;
  readonly label: string;
  readonly placeholder?: string;
  readonly autoComplete?: string;
  readonly type?: string;
  readonly full?: boolean;
  readonly maxLength?: number;
}

interface ModalSpec {
  readonly title: string;
  readonly copy?: string;
  readonly cta: string;
  readonly danger?: boolean;
  readonly fields: readonly ModalField[];
  readonly initial?: Readonly<Record<string, string>>;
  /** Field errors from the form's own checks; empty means go ahead. */
  validate?(values: Readonly<Record<string, string>>): Record<string, string>;
  /** Does the work. A thrown error is shown in the dialog, which stays open. */
  submit(values: Readonly<Record<string, string>>): Promise<void>;
}

function Modal({ spec, onClose }: { readonly spec: ModalSpec; readonly onClose: () => void }): JSX.Element {
  const [values, setValues] = useState<Record<string, string>>({ ...spec.initial });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [failure, setFailure] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    const first = formRef.current?.querySelector<HTMLElement>('input, button[type="submit"]');
    first?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    if (busy) return;
    const found = spec.validate?.(values) ?? {};
    if (Object.keys(found).length > 0) {
      setErrors(found);
      return;
    }
    setBusy(true);
    setFailure(undefined);
    spec
      .submit(values)
      .then(() => onClose())
      .catch((error: unknown) => {
        setFailure(messageOf(error));
        setBusy(false);
      });
  };

  return (
    <>
      <div className="jt-acc-scrim" onClick={onClose} aria-hidden="true" />
      <form
        ref={formRef}
        className="jt-acc-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="jt-acc-modal-title"
        onSubmit={onSubmit}
        noValidate
      >
        <div className="jt-acc-modal__head">
          <span id="jt-acc-modal-title" className="jt-acc-modal__title">
            {spec.title}
          </span>
          {spec.copy !== undefined ? <span className="jt-acc-modal__copy">{spec.copy}</span> : null}
        </div>
        {spec.fields.length > 0 ? (
          <div className="jt-acc-modal__fields">
            {spec.fields.map((field) => {
              const error = errors[field.key];
              const id = `jt-acc-m-${field.key}`;
              return (
                <div
                  key={field.key}
                  className={`jt-acc-mfield${field.full === true ? ' jt-acc-mfield--full' : ''}`}
                >
                  <label htmlFor={id} className="jt-acc-mfield__label">
                    {field.label}
                  </label>
                  <input
                    id={id}
                    {...(error !== undefined ? { 'aria-describedby': `${id}-err` } : {})}
                    className="jt-acc-mfield__input"
                    value={values[field.key] ?? ''}
                    type={field.type ?? 'text'}
                    placeholder={field.placeholder}
                    autoComplete={field.autoComplete ?? 'off'}
                    maxLength={field.maxLength ?? 200}
                    aria-invalid={error !== undefined}
                    onChange={(event) => {
                      const value = event.target.value;
                      setValues((previous) => ({ ...previous, [field.key]: value }));
                      setErrors((previous) => {
                        const { [field.key]: _gone, ...rest } = previous;
                        return rest;
                      });
                    }}
                  />
                  {error !== undefined ? (
                    <span id={`${id}-err`} className="jt-acc-err">
                      {error}
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : null}
        {failure !== undefined ? (
          <p className="jt-acc-err jt-acc-modal__failure" role="alert">
            {failure}
          </p>
        ) : null}
        <div className="jt-acc-modal__actions">
          <button type="button" className="jt-acc-btn jt-acc-btn--soft jt-acc-btn--lg" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className={`jt-acc-btn jt-acc-btn--lg ${spec.danger === true ? 'jt-acc-btn--danger' : 'jt-acc-btn--ink'}`}
            disabled={busy}
          >
            {busy ? 'Please wait…' : spec.cta}
          </button>
        </div>
      </form>
    </>
  );
}

// -- Page ------------------------------------------------------------------------

export function AccountPage({ user }: { readonly user: SessionUser }): JSX.Element {
  const navigator = useNavigator();
  const session = useSession();
  const wide = useWide();
  const section = sectionFrom(navigator.location.query.get('section'));

  const profile = useLoaded(getProfile);
  const inbox = useLoaded(getInbox);
  const consents = useLoaded(getConsents);
  const factors = useLoaded(getFactors);
  const wallet = useLoaded(getWallet);
  const orders = useLoaded(getDeviceOrderCount);
  const bookings = useLoaded(() => getUpcomingBookings());

  const [modal, setModal] = useState<ModalSpec | undefined>();
  const [toast, setToast] = useState('');
  const toastTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const flash = useCallback((message: string) => {
    clearTimeout(toastTimer.current);
    setToast(message);
    toastTimer.current = setTimeout(() => setToast(''), 2600);
  }, []);
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const closeModal = useCallback(() => setModal(undefined), []);

  const go = (id: AccountSection, event?: MouseEvent): void => {
    event?.preventDefault();
    setModal(undefined);
    navigator.navigate(sectionHref(id));
  };

  const name = realName(profile.value?.displayName) ?? realName(user.displayName);
  const first = name?.split(/\s+/)[0];
  const addressCount = profile.value?.addresses.length;
  const unread = inbox.value?.unread ?? 0;

  const meta: Partial<Record<AccountSection, string>> = {
    ...(addressCount !== undefined ? { addresses: `${String(addressCount)} saved` } : {}),
    payments: 'Coming soon',
    ...(unread > 0 ? { notifications: `${String(unread)} new` } : {}),
    saved: 'Coming soon',
  };

  const onBack = (event: MouseEvent<HTMLAnchorElement>): void => {
    if (section !== 'home') {
      go('home', event);
      return;
    }
    let sameSite = false;
    try {
      sameSite = document.referrer !== '' && new URL(document.referrer).origin === window.location.origin;
    } catch {
      sameSite = false;
    }
    event.preventDefault();
    if (sameSite && window.history.length > 1) window.history.back();
    else navigator.navigate(routes.home.path);
  };

  const navList = (items: readonly (typeof ACCOUNT_SECTIONS)[number][], compact: boolean): JSX.Element => (
    <div className="jt-acc-navcard">
      {items.map(([id, label, icon]) => {
        const current = !compact && section === id;
        return (
          <a
            key={id}
            href={sectionHref(id)}
            onClick={(event) => go(id, event)}
            aria-current={current ? 'page' : undefined}
            className={`jt-acc-nav${compact ? ' jt-acc-nav--compact' : ''}${current ? ' jt-acc-nav--on' : ''}`}
          >
            <span className="jt-acc-nav__icon">
              <Icon d={icon} />
            </span>
            <span className="jt-acc-nav__label">{label}</span>
            <span className="jt-acc-nav__meta">{meta[id] ?? ''}</span>
            {compact ? <Icon d="M9.4 5.4 16 12l-6.6 6.6" stroke="#8a7fb5" size={14} /> : null}
          </a>
        );
      })}
    </div>
  );

  let body: ReactNode;
  switch (section) {
    case 'home':
      body = (
        <Overview
          first={first}
          orders={orders}
          bookings={bookings}
          walletUnits={wallet.value?.availableUnits}
          walletLoading={wallet.loading}
          narrowNav={
            wide ? undefined : (
              <>
                {navList(ACCOUNT_SECTIONS.slice(1), true)}
                <MyClinicsEntry className="jt-acc-nav jt-acc-nav--compact jt-acc-nav--clinics" />
              </>
            )
          }
        />
      );
      break;
    case 'details':
      body = (
        <DetailsSection
          user={user}
          profile={profile}
          onSaved={(message, displayName) => {
            session.nameChanged(displayName);
            flash(message);
          }}
        />
      );
      break;
    case 'addresses':
      body = <AddressesSection profile={profile} setModal={setModal} flash={flash} />;
      break;
    case 'payments':
      body = (
        <SoonSection
          title="Payment methods"
          head="Saved cards are coming soon"
          copy="You can't save a card on Just Treated yet. When you can, card details will be stored by our payment provider, and Just Treated will never see your full card number."
        />
      );
      break;
    case 'notifications':
      body = <NotificationsSection inbox={inbox} consents={consents} flash={flash} />;
      break;
    case 'saved':
      body = (
        <SoonSection
          title="Saved"
          head="Saving is coming soon"
          copy="You can't save products or providers yet. When you can, they'll be listed here."
        />
      );
      break;
    case 'privacy':
      body = (
        <PrivacySection
          consents={consents}
          factors={factors}
          setModal={setModal}
          flash={flash}
          onLoggedOutEverywhere={async () => {
            await session.signOut();
            navigator.navigate(routes.logIn.path);
          }}
        />
      );
      break;
  }

  return (
    <>
      <a href={sectionHref('home')} className="jt-site-back" onClick={onBack}>
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M14.6 5.4 8 12l6.6 6.6" />
        </svg>
        {section !== 'home' ? 'Account' : 'Back'}
      </a>

      <div className={`jt-acc${wide ? '' : ' jt-acc--narrow'}`}>
        {wide ? (
          <nav aria-label="Account sections" className="jt-acc-side">
            <div className="jt-acc-me">
              <span className="jt-acc-me__avatar" aria-hidden="true">
                {initialsOf(user)}
              </span>
              <span className="jt-acc-me__text">
                <span className="jt-acc-me__name">{name ?? user.email}</span>
                {name !== undefined ? <span className="jt-acc-me__email">{user.email}</span> : null}
              </span>
            </div>
            {navList(ACCOUNT_SECTIONS, false)}
            {/* Clinics A14: only for a person a clinic has linked as a patient; nothing for anyone else. */}
            <MyClinicsEntry className="jt-acc-nav jt-acc-nav--clinics" />
          </nav>
        ) : null}

        <div className="jt-acc-body">{body}</div>
      </div>

      {modal !== undefined ? <Modal spec={modal} onClose={closeModal} /> : null}
      {toast !== '' ? (
        <div className="jt-acc-toast" role="status">
          {toast}
        </div>
      ) : null}
    </>
  );
}

// -- Sections ----------------------------------------------------------------------

function H1({ children }: { readonly children: ReactNode }): JSX.Element {
  return <h1 className="jt-acc-h1">{children}</h1>;
}

function LoadFailed({
  what,
  loaded,
}: {
  readonly what: string;
  readonly loaded: Loaded<unknown>;
}): JSX.Element {
  return (
    <div className="jt-acc-panel jt-acc-panel--pad" role="alert">
      <span className="jt-acc-panel__head">We couldn’t load {what}</span>
      <span className="jt-acc-panel__copy">{loaded.error}</span>
      <button type="button" className="jt-acc-btn jt-acc-btn--soft" onClick={loaded.reload}>
        Try again
      </button>
    </div>
  );
}

function Soon(): JSX.Element {
  return <span className="jt-acc-soon">Coming soon</span>;
}

function SoonSection({
  title,
  head,
  copy,
}: {
  readonly title: string;
  readonly head: string;
  readonly copy: string;
}): JSX.Element {
  return (
    <>
      <div className="jt-acc-titlerow">
        <H1>{title}</H1>
        <Soon />
      </div>
      <div className="jt-acc-panel jt-acc-panel--empty" role="status">
        <span className="jt-acc-panel__head">{head}</span>
        <span className="jt-acc-panel__copy">{copy}</span>
      </div>
    </>
  );
}

interface Tile {
  readonly key: 'orders' | 'bookings' | 'prescriptions' | 'rewards';
  readonly t: string;
  readonly d: string;
  readonly icon: string;
  readonly bg: string;
  readonly fg: string;
}

function Overview({
  first,
  orders,
  bookings,
  walletUnits,
  walletLoading,
  narrowNav,
}: {
  readonly first: string | undefined;
  readonly orders: Loaded<number>;
  readonly bookings: Loaded<{ count: number; atLeast: boolean }>;
  readonly walletUnits: number | undefined;
  readonly walletLoading: boolean;
  readonly narrowNav: JSX.Element | undefined;
}): JSX.Element {
  const ordersText =
    orders.value === undefined
      ? orders.loading
        ? 'Loading…'
        : 'Your orders'
      : orders.value === 0
        ? 'None yet'
        : `${String(orders.value)}${orders.value >= ORDERS_PAGE ? '+' : ''} ${orders.value === 1 ? 'order' : 'orders'}`;
  const bookingsText =
    bookings.value === undefined
      ? bookings.loading
        ? 'Loading…'
        : 'Appointments and consultations'
      : bookings.value.count === 0
        ? 'None upcoming'
        : `${String(bookings.value.count)}${bookings.value.atLeast ? '+' : ''} upcoming`;
  const rewardsText =
    walletUnits === undefined
      ? walletLoading
        ? 'Loading…'
        : 'Your PENNY points'
      : `${walletUnits.toLocaleString('en-GB')} PENNY`;

  const tiles: readonly Tile[] = [
    { key: 'orders', t: 'Orders', d: ordersText, icon: IC.box, bg: '#f0ecfa', fg: '#5b2ede' },
    { key: 'bookings', t: 'Bookings', d: bookingsText, icon: IC.cal, bg: '#e9f6ee', fg: '#0e6f5c' },
    {
      key: 'prescriptions',
      t: 'Prescriptions',
      // Stage 10: built. A plain line, not a count the tile would have to load.
      d: routes.prescriptions.availability === 'available' ? 'Uploads and repeat requests' : 'Coming soon',
      icon: IC.rx,
      bg: '#e7f6fa',
      fg: '#0d7d9c',
    },
    { key: 'rewards', t: 'Rewards', d: rewardsText, icon: IC.star, bg: '#fdeef0', fg: '#c0396a' },
  ];

  return (
    <>
      <h1 className="jt-acc-h1 jt-acc-h1--hi">{first !== undefined ? `Hi, ${first}` : 'Hi there'}</h1>
      <div className="jt-acc-tiles">
        {tiles.map((tile) => {
          const live = routes[tile.key].availability === 'available';
          const inner = (
            <>
              <span className="jt-acc-tile__top">
                <span className="jt-acc-tile__icon" style={{ background: tile.bg }} aria-hidden="true">
                  <Icon d={tile.icon} stroke={tile.fg} size={20} />
                </span>
                {live || tile.d === 'Coming soon' ? null : <Soon />}
              </span>
              <span className="jt-acc-tile__text">
                <span className="jt-acc-tile__t">{tile.t}</span>
                <span className="jt-acc-tile__d">{tile.d}</span>
              </span>
            </>
          );
          return live ? (
            <AppLink key={tile.key} to={routes[tile.key].path} className="jt-acc-tile jt-acc-tile--link">
              {inner}
            </AppLink>
          ) : (
            <div key={tile.key} className="jt-acc-tile" aria-disabled="true">
              {inner}
            </div>
          );
        })}
      </div>
      {narrowNav}
    </>
  );
}

const MOBILE_DIGITS = (value: string): string => value.replace(/\D/g, '');

function DetailsSection({
  user,
  profile,
  onSaved,
}: {
  readonly user: SessionUser;
  readonly profile: Loaded<Awaited<ReturnType<typeof getProfile>>>;
  readonly onSaved: (message: string, displayName: string | undefined) => void;
}): JSX.Element {
  const p = profile.value;
  const email =
    p?.contacts.find((c) => c.channel === 'email' && c.source === 'identity') ??
    p?.contacts.find((c) => c.channel === 'email' && c.isPrimary) ??
    p?.contacts.find((c) => c.channel === 'email');
  const mobile =
    p?.contacts.find((c) => c.channel === 'sms') ?? p?.contacts.find((c) => c.channel === 'phone');

  const loaded = {
    name: realName(p?.displayName) ?? '',
    phone: mobile?.value ?? '',
    dob: displayDate(p?.dateOfBirth),
  };
  const [draft, setDraft] = useState<typeof loaded | undefined>();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [failure, setFailure] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  if (p === undefined) {
    return (
      <>
        <H1>Personal details</H1>
        {profile.error !== undefined ? (
          <LoadFailed what="your details" loaded={profile} />
        ) : (
          <div className="jt-acc-panel jt-acc-panel--pad" aria-busy="true">
            <span className="jt-acc-panel__copy">Loading your details…</span>
          </div>
        )}
      </>
    );
  }

  const d = draft ?? loaded;
  const clean =
    draft === undefined || (Object.keys(loaded) as (keyof typeof loaded)[]).every((k) => d[k] === loaded[k]);
  const set =
    (key: keyof typeof loaded) =>
    (value: string): void => {
      setDraft({ ...d, [key]: value });
      setErrors(({ [key]: _gone, ...rest }) => rest);
      setFailure(undefined);
    };

  const save = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (clean || busy) return;
    const found: Record<string, string> = {};
    if (d.name.trim().length < 2) found.name = 'Enter your full name';
    const phoneChanged = d.phone.trim() !== loaded.phone.trim();
    if (phoneChanged && MOBILE_DIGITS(d.phone).length < 10) found.phone = 'Enter a UK mobile number';
    const dobChanged = d.dob.trim() !== loaded.dob;
    const iso = dobChanged ? isoDate(d.dob) : undefined;
    if (dobChanged && iso === undefined) found.dob = 'Use DD/MM/YYYY';
    if (Object.keys(found).length > 0) {
      setErrors(found);
      return;
    }

    setBusy(true);
    setFailure(undefined);
    try {
      const nameChanged = d.name.trim() !== loaded.name;
      if (nameChanged || iso !== undefined) {
        await updateProfile({
          ...(nameChanged ? { displayName: d.name.trim() } : {}),
          ...(iso !== undefined ? { dateOfBirth: iso } : {}),
        });
      }
      if (phoneChanged) {
        // A number is never edited in place: the new one is added and the old
        // one removed, so a verification earned by one number never follows
        // the person to another (the platform refuses value edits for this).
        await addContact('sms', d.phone.trim());
        if (mobile !== undefined && mobile.source === 'profile') await removeContact(mobile.contactPointId);
      }
      setDraft(undefined);
      profile.reload();
      onSaved(
        phoneChanged ? 'Details saved. Your new mobile number is not verified yet.' : 'Details saved',
        d.name.trim(),
      );
    } catch (error) {
      setFailure(messageOf(error));
    } finally {
      setBusy(false);
    }
  };

  const fields: readonly {
    key: 'name' | 'email' | 'phone' | 'dob';
    label: string;
    value: string;
    type: string;
    ac: string;
    verified: boolean;
    help?: string;
    readOnly?: boolean;
  }[] = [
    { key: 'name', label: 'Full name', value: d.name, type: 'text', ac: 'name', verified: false },
    {
      key: 'email',
      label: 'Email',
      value: email?.value ?? user.email,
      type: 'email',
      ac: 'email',
      verified: email?.verifiedAt !== undefined,
      help: 'This is the email you log in with. Changing it isn’t available yet.',
      readOnly: true,
    },
    {
      key: 'phone',
      label: 'Mobile',
      value: d.phone,
      type: 'tel',
      ac: 'tel',
      verified: mobile?.verifiedAt !== undefined && d.phone === loaded.phone,
      help: 'Used for delivery updates and appointment reminders',
    },
    {
      key: 'dob',
      label: 'Date of birth',
      value: d.dob,
      type: 'text',
      ac: 'bday',
      verified: false,
      help: 'Needed for age-restricted medicines and some services',
    },
  ];

  return (
    <>
      <H1>Personal details</H1>
      <form className="jt-acc-form" onSubmit={(event) => void save(event)} noValidate>
        <div className="jt-acc-form__grid">
          {fields.map((f) => {
            const error = errors[f.key];
            const id = `jt-acc-f-${f.key}`;
            const described = [
              error !== undefined ? `${id}-err` : '',
              f.help !== undefined ? `${id}-help` : '',
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <div key={f.key} className="jt-acc-field">
                <span className="jt-acc-field__label">
                  <label htmlFor={id}>{f.label}</label>
                  {f.verified ? <span className="jt-acc-chip jt-acc-chip--ok">VERIFIED</span> : null}
                </span>
                <input
                  id={id}
                  {...(described !== '' ? { 'aria-describedby': described } : {})}
                  className="jt-acc-field__input"
                  value={f.value}
                  type={f.type}
                  autoComplete={f.ac}
                  readOnly={f.readOnly === true}
                  placeholder={
                    f.key === 'dob' ? 'DD/MM/YYYY' : f.key === 'phone' ? 'Add a mobile number' : undefined
                  }
                  aria-invalid={error !== undefined}
                  onChange={(event) => {
                    if (f.key !== 'email') set(f.key)(event.target.value);
                  }}
                />
                {error !== undefined ? (
                  <span id={`${id}-err`} className="jt-acc-err">
                    {error}
                  </span>
                ) : null}
                {f.help !== undefined ? (
                  <span id={`${id}-help`} className="jt-acc-help">
                    {f.help}
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>
        {failure !== undefined ? (
          <p className="jt-acc-err" role="alert">
            {failure}
          </p>
        ) : null}
        <div className="jt-acc-form__actions">
          <button
            type="button"
            className="jt-acc-btn jt-acc-btn--soft jt-acc-btn--lg"
            disabled={clean || busy}
            onClick={() => {
              setDraft(undefined);
              setErrors({});
              setFailure(undefined);
            }}
          >
            Discard changes
          </button>
          <button
            type="submit"
            className="jt-acc-btn jt-acc-btn--ink jt-acc-btn--lg"
            disabled={clean || busy}
          >
            {busy ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </>
  );
}

function addressModal(existing: Address | undefined, after: (message: string) => void): ModalSpec {
  return {
    title: existing === undefined ? 'Add an address' : 'Edit address',
    cta: 'Save address',
    fields: [
      { key: 'label', label: 'Name it', placeholder: 'e.g. Home, Mum’s', full: true, maxLength: 60 },
      {
        key: 'l1',
        label: 'Address',
        placeholder: 'House number and street',
        autoComplete: 'address-line1',
        full: true,
      },
      { key: 'town', label: 'Town or city', autoComplete: 'address-level2', maxLength: 100 },
      { key: 'pc', label: 'Postcode', autoComplete: 'postal-code', maxLength: 10 },
      {
        key: 'note',
        label: 'Note for couriers (optional)',
        placeholder: 'e.g. Ring the top bell',
        full: true,
        maxLength: 500,
      },
    ],
    initial:
      existing === undefined
        ? {}
        : {
            label: existing.label ?? '',
            l1: existing.line1,
            town: existing.city,
            pc: existing.postcode,
            note: existing.deliveryInstructions ?? '',
          },
    validate: (v) => {
      const e: Record<string, string> = {};
      if ((v.l1 ?? '').trim() === '') e.l1 = 'Enter the first line';
      if ((v.town ?? '').trim() === '') e.town = 'Enter a town';
      if (!UK_POSTCODE.test((v.pc ?? '').trim())) e.pc = 'Enter a valid UK postcode';
      return e;
    },
    submit: async (v) => {
      const label = (v.label ?? '').trim();
      const note = (v.note ?? '').trim();
      // Fields this form does not show are carried over, so editing never drops them.
      const body: AddressInput = {
        ...(existing?.recipientName !== undefined ? { recipientName: existing.recipientName } : {}),
        ...(existing?.line2 !== undefined ? { line2: existing.line2 } : {}),
        ...(existing?.region !== undefined ? { region: existing.region } : {}),
        ...(label !== '' ? { label } : {}),
        line1: (v.l1 ?? '').trim(),
        city: (v.town ?? '').trim(),
        postcode: (v.pc ?? '').trim().toUpperCase(),
        countryCode: existing?.countryCode ?? 'GB',
        ...(note !== '' ? { deliveryInstructions: note } : {}),
      };
      if (existing === undefined) await addAddress(body);
      else await editAddress(existing.addressId, body);
      after(existing === undefined ? 'Address added' : 'Address updated');
    },
  };
}

function AddressesSection({
  profile,
  setModal,
  flash,
}: {
  readonly profile: Loaded<Awaited<ReturnType<typeof getProfile>>>;
  readonly setModal: (spec: ModalSpec) => void;
  readonly flash: (message: string) => void;
}): JSX.Element {
  const [failure, setFailure] = useState<string | undefined>();
  const addresses = profile.value?.addresses;
  const done = (message: string): void => {
    profile.reload();
    flash(message);
  };

  return (
    <>
      <div className="jt-acc-titlerow">
        <H1>Addresses</H1>
        <button
          type="button"
          className="jt-acc-btn jt-acc-btn--ink jt-acc-btn--md"
          onClick={() => setModal(addressModal(undefined, done))}
        >
          Add an address
        </button>
      </div>
      {failure !== undefined ? (
        <p className="jt-acc-err" role="alert">
          {failure}
        </p>
      ) : null}
      {addresses === undefined ? (
        profile.error !== undefined ? (
          <LoadFailed what="your addresses" loaded={profile} />
        ) : (
          <div className="jt-acc-panel jt-acc-panel--pad" aria-busy="true">
            <span className="jt-acc-panel__copy">Loading your addresses…</span>
          </div>
        )
      ) : addresses.length === 0 ? (
        <div className="jt-acc-panel jt-acc-panel--empty">
          <span className="jt-acc-panel__head">No saved addresses</span>
          <span className="jt-acc-panel__copy">Add one and it’s ready when you order.</span>
        </div>
      ) : (
        addresses.map((a) => {
          const label = a.label ?? 'Address';
          return (
            <article key={a.addressId} className={`jt-acc-row${a.isDefault ? ' jt-acc-row--default' : ''}`}>
              <span className="jt-acc-row__icon">
                <Icon d={addressIcon(label)} size={20} />
              </span>
              <span className="jt-acc-row__text">
                <span className="jt-acc-row__title">
                  {label}
                  {a.isDefault ? <span className="jt-acc-chip">DEFAULT</span> : null}
                </span>
                <span className="jt-acc-row__line">{addressLine(a)}</span>
                {a.deliveryInstructions !== undefined && a.deliveryInstructions !== '' ? (
                  <span className="jt-acc-row__note">Note: {a.deliveryInstructions}</span>
                ) : null}
              </span>
              <span className="jt-acc-row__actions">
                {a.isDefault ? null : (
                  <button
                    type="button"
                    className="jt-acc-btn jt-acc-btn--soft"
                    onClick={() => {
                      setFailure(undefined);
                      makeDefaultAddress(a.addressId)
                        .then(() => done(`${label} is now your default`))
                        .catch((error: unknown) => setFailure(messageOf(error)));
                    }}
                  >
                    Make default
                  </button>
                )}
                <button
                  type="button"
                  className="jt-acc-btn jt-acc-btn--soft"
                  onClick={() => setModal(addressModal(a, done))}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="jt-acc-btn jt-acc-btn--remove"
                  aria-label={`Remove ${label}`}
                  onClick={() =>
                    setModal({
                      title: `Remove ${label}?`,
                      copy: addressLine(a),
                      cta: 'Remove',
                      danger: true,
                      fields: [],
                      submit: async () => {
                        await removeAddress(a.addressId);
                        done('Address removed');
                      },
                    })
                  }
                >
                  Remove
                </button>
              </span>
            </article>
          );
        })
      )}
    </>
  );
}

function Switch({
  on,
  label,
  busy,
  onToggle,
  size = 'sm',
}: {
  readonly on: boolean;
  readonly label: string;
  readonly busy: boolean;
  readonly onToggle: () => void;
  readonly size?: 'sm' | 'lg';
}): JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      aria-busy={busy}
      disabled={busy}
      onClick={onToggle}
      className={`jt-acc-switch jt-acc-switch--${size}${on ? ' jt-acc-switch--on' : ''}`}
    >
      <span className="jt-acc-switch__knob" />
    </button>
  );
}

/**
 * A consent switch moves only when the server says it moved: the write
 * happens first, then the record is read back. A switch that flipped and then
 * quietly failed would leave somebody believing they had opted out.
 */
function useConsentToggle(
  consents: Loaded<readonly { purpose: string; channel?: string | null; isActive: boolean }[]>,
  flash: (m: string) => void,
) {
  const [busy, setBusy] = useState<string | undefined>();
  const [failure, setFailure] = useState<string | undefined>();
  const active = (purpose: string, channel?: string): boolean =>
    (consents.value ?? []).some(
      (c) => c.isActive && c.purpose === purpose && (c.channel ?? undefined) === channel,
    );
  const toggle = (purpose: 'marketing' | 'analytics', channel: string | undefined, saved?: string): void => {
    const key = `${purpose}:${channel ?? '-'}`;
    const on = active(purpose, channel);
    setBusy(key);
    setFailure(undefined);
    (on ? withdrawConsent(purpose, channel) : grantConsent(purpose, channel))
      .then(() => {
        consents.reload();
        if (saved !== undefined) flash(saved);
      })
      .catch((error: unknown) => setFailure(messageOf(error)))
      .finally(() => setBusy(undefined));
  };
  return { active, toggle, busy, failure };
}

const TRANSACTIONAL_ROWS = [
  ['Order updates', 'Confirmations, delivery tracking and refunds'],
  ['Appointment reminders', 'Confirmations and changes to your appointments'],
  ['Prescription updates', 'When your prescription is ready'],
] as const;

const CHANNELS = [
  ['email', 'Email'],
  ['sms', 'SMS'],
  ['push', 'Push'],
] as const;

function NotificationsSection({
  inbox,
  consents,
  flash,
}: {
  readonly inbox: Loaded<Awaited<ReturnType<typeof getInbox>>>;
  readonly consents: Loaded<Awaited<ReturnType<typeof getConsents>>>;
  readonly flash: (message: string) => void;
}): JSX.Element {
  const consent = useConsentToggle(consents, flash);
  const [failure, setFailure] = useState<string | undefined>();
  const items = inbox.value?.notifications.slice(0, 8);

  return (
    <>
      <H1>Notifications</H1>
      <section aria-label="Recent" className="jt-acc-panel jt-acc-inbox">
        <div className="jt-acc-inbox__head">
          <span className="jt-acc-inbox__title">Recent</span>
          <button
            type="button"
            className="jt-acc-link"
            disabled={(inbox.value?.unread ?? 0) === 0}
            onClick={() => {
              setFailure(undefined);
              readAll()
                .then(() => {
                  inbox.reload();
                  flash('All marked as read');
                })
                .catch((error: unknown) => setFailure(messageOf(error)));
            }}
          >
            Mark all as read
          </button>
        </div>
        {failure !== undefined ? (
          <p className="jt-acc-err" role="alert">
            {failure}
          </p>
        ) : null}
        {items === undefined ? (
          inbox.error !== undefined ? (
            <div className="jt-acc-inbox__empty" role="alert">
              We couldn’t load your notifications.{' '}
              <button type="button" className="jt-acc-link" onClick={inbox.reload}>
                Try again
              </button>
            </div>
          ) : (
            <div className="jt-acc-inbox__empty">Loading…</div>
          )
        ) : items.length === 0 ? (
          <div className="jt-acc-inbox__empty">
            Nothing yet. Updates about your orders and appointments appear here.
          </div>
        ) : (
          items.map((m) => {
            const unread = m.readAt === undefined;
            const text = (
              <>
                <span
                  className={`jt-acc-inbox__dot${unread ? ' jt-acc-inbox__dot--on' : ''}`}
                  aria-hidden="true"
                />
                <span className="jt-acc-inbox__text">
                  <span className={`jt-acc-inbox__t${unread ? ' jt-acc-inbox__t--unread' : ''}`}>
                    {m.title}
                  </span>
                  <span className="jt-acc-inbox__d">
                    {[m.body, when(m.createdAt)].filter(Boolean).join(' · ')}
                  </span>
                </span>
              </>
            );
            return unread ? (
              <button
                key={m.id}
                type="button"
                className="jt-acc-inbox__row"
                aria-label={`${m.title}. Unread — mark as read`}
                onClick={() => {
                  readOne(m.id)
                    .then(() => inbox.reload())
                    .catch((error: unknown) => setFailure(messageOf(error)));
                }}
              >
                {text}
              </button>
            ) : (
              <div key={m.id} className="jt-acc-inbox__row">
                {text}
              </div>
            );
          })
        )}
      </section>

      <section aria-label="Preferences" className="jt-acc-panel jt-acc-prefs">
        <div className="jt-acc-prefs__grid jt-acc-prefs__head">
          <span>SEND ME</span>
          {CHANNELS.map(([, label]) => (
            <span key={label} className="jt-acc-prefs__col">
              {label.toUpperCase()}
            </span>
          ))}
        </div>
        {TRANSACTIONAL_ROWS.map(([t, d]) => (
          <div key={t} className="jt-acc-prefs__grid jt-acc-prefs__row">
            <span className="jt-acc-prefs__text">
              <span className="jt-acc-prefs__t">{t}</span>
              <span className="jt-acc-prefs__d">{d}</span>
            </span>
            <span className="jt-acc-prefs__always">Always sent</span>
          </div>
        ))}
        <div className="jt-acc-prefs__grid jt-acc-prefs__row">
          <span className="jt-acc-prefs__text">
            <span className="jt-acc-prefs__t">Offers and news</span>
            <span className="jt-acc-prefs__d">Deals from providers near you.</span>
          </span>
          {CHANNELS.map(([channel, label]) => (
            <span key={channel} className="jt-acc-prefs__cell">
              {consents.value === undefined ? (
                <span className="jt-acc-prefs__wait">{consents.error !== undefined ? '!' : '…'}</span>
              ) : (
                <Switch
                  on={consent.active('marketing', channel)}
                  label={`Offers and news by ${label}`}
                  busy={consent.busy === `marketing:${channel}`}
                  onToggle={() => consent.toggle('marketing', channel)}
                />
              )}
            </span>
          ))}
        </div>
        <p className="jt-acc-prefs__note">
          Messages about your orders, appointments and prescriptions are always sent, so you never miss your
          medicine. You can only turn off offers.
        </p>
        {consent.failure !== undefined ? (
          <p className="jt-acc-err" role="alert">
            {consent.failure}
          </p>
        ) : null}
        {consents.error !== undefined ? (
          <p className="jt-acc-err" role="alert">
            We couldn’t load your choices.{' '}
            <button type="button" className="jt-acc-link" onClick={consents.reload}>
              Try again
            </button>
          </p>
        ) : null}
      </section>
    </>
  );
}

function PrivacySection({
  consents,
  factors,
  setModal,
  flash,
  onLoggedOutEverywhere,
}: {
  readonly consents: Loaded<Awaited<ReturnType<typeof getConsents>>>;
  readonly factors: Loaded<Awaited<ReturnType<typeof getFactors>>>;
  readonly setModal: (spec: ModalSpec) => void;
  readonly flash: (message: string) => void;
  readonly onLoggedOutEverywhere: () => Promise<void>;
}): JSX.Element {
  const consent = useConsentToggle(consents, flash);
  const twoStepOn = (factors.value ?? []).some((f) => f.status === 'confirmed');
  const twoStep =
    factors.value === undefined
      ? factors.loading
        ? 'Checking…'
        : 'We couldn’t check this just now.'
      : twoStepOn
        ? 'On. An authenticator app asks for a code when you log in with a password.'
        : 'Off. Setting it up on the website is coming soon.';

  const rows: readonly {
    t: string;
    d: string;
    action: ReactNode;
  }[] = [
    {
      t: 'Password',
      d: 'If you use one. You can always log in with a code sent to your email instead.',
      action: (
        <button
          type="button"
          className="jt-acc-btn jt-acc-btn--soft jt-acc-btn--row"
          onClick={() =>
            setModal({
              title: 'Change password',
              copy: 'Use at least 12 characters. You’ll stay logged in here and be logged out everywhere else.',
              cta: 'Update password',
              fields: [
                {
                  key: 'cur',
                  label: 'Current password',
                  type: 'password',
                  autoComplete: 'current-password',
                  full: true,
                  maxLength: 256,
                },
                {
                  key: 'np',
                  label: 'New password',
                  type: 'password',
                  autoComplete: 'new-password',
                  full: true,
                  maxLength: 256,
                },
              ],
              validate: (v) => {
                const e: Record<string, string> = {};
                if ((v.cur ?? '') === '') e.cur = 'Enter your current password';
                if ((v.np ?? '').length < 12) e.np = 'Use at least 12 characters';
                return e;
              },
              submit: async (v) => {
                await changePassword(v.cur ?? '', v.np ?? '');
                flash('Password updated');
              },
            })
          }
        >
          Change
        </button>
      ),
    },
    { t: 'Two-step verification', d: twoStep, action: <Soon /> },
    { t: 'Marketing from providers', d: 'Let providers you’ve used send you offers', action: <Soon /> },
    {
      t: 'Help improve Just Treated',
      d: 'Lets us count how Just Treated is used. Never sold, and never tied to your health.',
      action:
        consents.value === undefined ? (
          <span className="jt-acc-prefs__wait">{consents.error !== undefined ? 'Unavailable' : '…'}</span>
        ) : (
          <Switch
            size="lg"
            on={consent.active('analytics')}
            label="Help improve Just Treated"
            busy={consent.busy === 'analytics:-'}
            onToggle={() => consent.toggle('analytics', undefined)}
          />
        ),
    },
    {
      t: 'Download my data',
      d: 'A copy of your orders, bookings and health answers.',
      action: <Soon />,
    },
    {
      t: 'Log out of all devices',
      d: 'Including the apps on your phone',
      action: (
        <button
          type="button"
          className="jt-acc-btn jt-acc-btn--soft jt-acc-btn--row"
          onClick={() =>
            setModal({
              title: 'Log out of all devices?',
              copy: 'You’ll need to log in again everywhere, including here.',
              cta: 'Log out everywhere',
              danger: true,
              fields: [],
              submit: async () => {
                await logoutOthers();
                await onLoggedOutEverywhere();
              },
            })
          }
        >
          Log out everywhere
        </button>
      ),
    },
    {
      t: 'Delete account',
      d: 'We keep prescription and pharmacy records for as long as the law requires.',
      action: <Soon />,
    },
  ];

  return (
    <>
      <H1>Privacy and security</H1>
      <section className="jt-acc-panel jt-acc-privacy">
        {rows.map((r) => (
          <div key={r.t} className="jt-acc-privacy__row">
            <span className="jt-acc-privacy__text">
              <span className="jt-acc-privacy__t">{r.t}</span>
              <span className="jt-acc-privacy__d">{r.d}</span>
            </span>
            {r.action}
          </div>
        ))}
      </section>
      {consent.failure !== undefined ? (
        <p className="jt-acc-err" role="alert">
          {consent.failure}
        </p>
      ) : null}
    </>
  );
}
